import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { colors } from '../constants/colors';

/**
 * Arc-shaped category bar.
 *
 * The dome is a static backdrop and the tab row scrolls *inside* it, each tab
 * rising as it passes the middle — so the arc behaves like a rail the tabs
 * slide along, not a shape painted behind them. That distinction only shows up
 * once there are more tabs than fit on screen, which is exactly where the
 * previous index-based lift broke down: it measured each tab's distance from
 * the centre of the *list*, so on a long list the outer tabs sat flat and the
 * curve fell apart. Here the lift is measured from the centre of the *viewport*
 * and recomputed as you scroll, so the curve holds at any length.
 *
 * Lift and dome share one equation. A symmetric quadratic bezier rises
 * 4t(1-t) of its apex height above its endpoints, so a tab at horizontal
 * fraction t of the dome's span sits exactly on the drawn curve.
 */

// Dome geometry in px. The curve's ends sit MAX_LIFT below its apex, which is
// also how far the centre tab rises above the outermost ones.
const MAX_LIFT = 21;
// Because the tabs ride a curve of the same shape, the dome's edge clears the
// top of EVERY circle by the same margin: track padding-top (40, in app.css)
// minus EDGE_Y = 12px. Keep those two numbers in sync — if the curve edge ends
// up below the padding again, the circles poke out of the frame (the "tabs
// floating off the colour-frame" look this replaced, Aug 2026).
const APEX_Y = 7;
const EDGE_Y = APEX_Y + MAX_LIFT;
// Control point that puts the curve's midpoint exactly at APEX_Y.
const CTRL_Y = 2 * APEX_Y - EDGE_Y;
// Total dome height. The tail runs well past the tabs so the fill can fade out
// behind the first row of product cards instead of ending on a hard edge.
const DOME_H = 240;
// Cancels the page container's 20px side padding so the dome is full-bleed.
const BLEED = 20;

const liftAt = (t) => MAX_LIFT * 4 * t * (1 - t);

const CategoryArcBar = ({ tabs, selected, onSelect, icons = {}, images = {} }) => {
  const trackRef = useRef(null);
  const centersRef = useRef([]);
  const rafRef = useRef(0);
  const [view, setView] = useState({ scrollLeft: 0, width: 0, scrollWidth: 0 });

  // Cache each tab's centre once per layout. Reading offsetLeft every scroll
  // frame would be wasted work — only scrollLeft changes while sliding.
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    centersRef.current = Array.from(track.children).map(
      (el) => el.offsetLeft + el.offsetWidth / 2
    );
    setView({
      scrollLeft: track.scrollLeft,
      width: track.clientWidth,
      scrollWidth: track.scrollWidth,
    });
  }, []);

  useLayoutEffect(() => { measure(); }, [measure, tabs]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // Scroll fires far more often than we can paint, so coalesce to one update
  // per frame. The lift must not be CSS-transitioned (see app.css) or it
  // smears behind the finger instead of tracking it.
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const track = trackRef.current;
      if (track) setView((prev) => ({ ...prev, scrollLeft: track.scrollLeft }));
    });
  }, []);

  // Bring the chosen tab to the middle of the dome — it is both the visual
  // high point and, on a long list, possibly off-screen when tapped.
  const centerTab = (el) => {
    if (!el || !el.scrollIntoView) return;
    // `block: 'nearest'` keeps this from scrolling the whole page vertically.
    el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const domeSpan = view.width + BLEED * 2;
  const viewCenter = view.scrollLeft + view.width / 2;

  const liftOf = (i) => {
    const center = centersRef.current[i];
    if (center == null || !view.width) return 0;
    // Position within the dome's span, which starts BLEED px left of the track.
    const t = (center - view.scrollLeft + BLEED) / domeSpan;
    return liftAt(Math.min(1, Math.max(0, t)));
  };

  const scrollable = view.scrollWidth - view.width > 2;
  const atStart = view.scrollLeft <= 2;
  const atEnd = view.scrollLeft + view.width >= view.scrollWidth - 2;

  const domePath =
    `M 0,${EDGE_Y} Q ${domeSpan / 2},${CTRL_Y} ${domeSpan},${EDGE_Y} ` +
    `L ${domeSpan},${DOME_H} L 0,${DOME_H} Z`;

  return (
    <div style={{ position: 'relative' }}>
      {/* Dome. Fades to transparent at the bottom so it dissolves into the
          product grid rather than stopping on a visible band edge. */}
      <svg
        viewBox={`0 0 ${domeSpan || 1} ${DOME_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: `-${BLEED}px`,
          width: `calc(100% + ${BLEED * 2}px)`,
          height: `${DOME_H}px`,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <defs>
          <linearGradient id="arcDomeFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.gold100} stopOpacity="1" />
            <stop offset="45%" stopColor={colors.gold100} stopOpacity="0.92" />
            <stop offset="100%" stopColor={colors.gold100} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={domePath} fill="url(#arcDomeFade)" />
      </svg>

      <div
        className="category-bar-track"
        ref={trackRef}
        onScroll={handleScroll}
        role="tablist"
        aria-label="Menu categories"
      >
        {tabs.map((tab, idx) => {
          const isActive = selected === tab.value;
          return (
            <button
              key={tab.label}
              role="tab"
              aria-selected={isActive}
              onClick={(e) => {
                onSelect(tab.value);
                centerTab(e.currentTarget);
              }}
              className="category-tab-btn"
              style={{ transform: `translateY(-${liftOf(idx).toFixed(2)}px)` }}
            >
              <div
                className="category-tab-circle"
                style={{
                  boxShadow: isActive
                    ? '0 4px 16px rgba(0,0,0,0.12)'
                    : '0 2px 10px rgba(0,0,0,0.07)',
                }}
              >
                {images[tab.label] ? (
                  <img src={images[tab.label]} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className="category-tab-emoji">{icons[tab.label] || '🍃'}</span>
                )}
              </div>
              <span
                className={`category-tab-label${isActive ? ' active' : ''}`}
                style={{ color: isActive ? colors.stone900 : colors.textMuted }}
              >
                {tab.label}
              </span>
              <div
                className="category-tab-underline"
                style={{ width: isActive ? '18px' : '0px' }}
              />
            </button>
          );
        })}
      </div>

      {/* Scroll affordance — only drawn on the side that still has tabs left,
          so a list that fits shows no chrome at all. */}
      {scrollable && !atStart && <div className="category-bar-fade start" aria-hidden="true" />}
      {scrollable && !atEnd && <div className="category-bar-fade end" aria-hidden="true" />}
    </div>
  );
};

export default CategoryArcBar;
